import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNav } from '../lib/router.js';
import { useStore } from '../lib/store.js';
import { Logo, useLook } from '../lib/look.js';
import { api } from '../lib/api.js';
import { apiUrl } from '../lib/platform.js';
import { IC, Svg } from '../art/icons.js';
import {
  INTRO_FADE_MS, INTRO_GLIDE_MS, INTRO_HOLD_MS, INTRO_SETTLE_CAP_MS, WELCOME_CARDS_AT_MS, WELCOME_TAGLINE_AT_MS,
  glideTransform, introKind, readLocalSeen, rememberSeen, welcomeDue, welcomeSync, type IntroKind,
} from '../lib/intro.js';

/*
 * The opening (CLAUDE.md §4 appearance, Saif 21 Oct 2026). index.html paints the launch screen's twin (#boot: the page
 * colour and the small centred mark) before any script runs; this takes it over in the same place, so the mark is one
 * element from launch to its home in the Home header: the amber glow blooms up from the bottom, the mark lifts and
 * glides while the page fades in beneath (html[data-intro] keeps #root at opacity 0 until then). About a second, once
 * per cold start, never waiting on data. Under Reduce Motion it is a plain fade; with the theme's motion off, nothing.
 * The first time ever (once per install, once per account) the mark holds instead, a tagline appears under it, then the
 * welcome cards with Join and Browse first; Skip is always there (sign-in and browsing are never blocked).
 */

const MARK_WIDTH = 96;
const MARK_HEIGHT = Math.round((MARK_WIDTH * 80) / 172); // logo.webp is 172 × 80

let played = false; // once per cold start (a module loads once)
let toldServer = false;

export function introPlanned(): boolean {
  return !played;
}

function centreRect(): { left: number; top: number; width: number } {
  return { left: Math.round((window.innerWidth - MARK_WIDTH) / 2), top: Math.round((window.innerHeight - MARK_HEIGHT) / 2), width: MARK_WIDTH };
}

export function Intro() {
  const s = useStore();
  const { v, t } = useLook();
  const { navigate } = useNav();
  const [state] = useState(() => {
    const coldStart = !played; // marked played in the mount effect below (a state initialiser must be pure: StrictMode runs it twice)
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const kind: IntroKind = introKind({ motion: v.theme.motion, reducedMotion: reduced, coldStart });
    const welcome = coldStart && welcomeDue({ enabled: v.welcome.enabled, cards: v.welcome.cards.length, localSeen: readLocalSeen(), serverSeen: null });
    return { kind, welcome, reduced, at: centreRect() };
  });
  const [phase, setPhase] = useState<'hold' | 'glide' | 'tagline' | 'cards' | 'leaving' | 'done'>(state.kind === 'none' && !state.welcome ? 'done' : 'hold');
  const [transform, setTransform] = useState<string>('');
  const [landed, setLanded] = useState(false);
  const [onCard, setOnCard] = useState(false); // the mark lands on the Tikkas card's logo: it takes that logo's ink // the glide ends off the header (no Home header on this screen): fade the mark out
  const leaving = useRef(false);
  // Read inside the glide's frame loop (the effect runs once): the account has answered, so Home shows the right card.
  const meLoadedRef = useRef(s.meLoaded);
  meLoadedRef.current = s.meLoaded;

  // #boot (index.html) hands over to the mark drawn here, in the same place, on the first paint.
  useEffect(() => {
    played = true;
    document.getElementById('boot')?.remove();
  }, []);
  // Done: the attribute goes in the same commit as the overlay, so the card's logo appears in the very paint the mark leaves (never two logos).
  useLayoutEffect(() => {
    if (phase !== 'done') return;
    document.documentElement.removeAttribute('data-intro');
    window.dispatchEvent(new Event('tikkas:intro-done'));
  }, [phase]);

  // The glide: hold, then travel to the header's logo while the page fades in; or the plain fade.
  useEffect(() => {
    if (state.welcome || state.kind === 'none') return;
    const html = document.documentElement;
    if (state.kind === 'fade') {
      html.setAttribute('data-intro', 'in');
      const t1 = setTimeout(() => setPhase('done'), INTRO_FADE_MS + 40);
      return () => clearTimeout(t1);
    }
    // The mark holds alone (the card's own logo is hidden while html[data-intro] is set), then glides onto the logo's box.
    // The page beneath is still settling in the first frames of a cold start: the account answers (the join card becomes the
    // Tikkas card), the fonts come in, and in the iPhone app the top safe-area inset arrives a few frames late, moving
    // everything down. So the glide starts only once the fonts are ready, the account is known (meLoaded) and the logo's box
    // has held still for two frames in a row, measured in the very frame the glide starts, so it lands to the pixel; a cap
    // keeps a slow network from holding the mark for ever. The card's logo turns visible in the same paint the mark goes
    // (phase 'done' drops the attribute, below).
    let cancelled = false;
    let t2 = 0;
    const measure = () => {
      const target = document.querySelector<HTMLElement>('[data-intro-target]');
      const r = target?.getBoundingClientRect();
      return r && r.width > 0 ? { r, onCard: !!target?.closest('.bankcard') } : null;
    };
    const same = (a: DOMRect, b: DOMRect) => Math.abs(a.left - b.left) < 0.5 && Math.abs(a.top - b.top) < 0.5 && Math.abs(a.width - b.width) < 0.5;
    const go = (m: ReturnType<typeof measure>) => {
      if (m) { setTransform(glideTransform(state.at, { left: m.r.left, top: m.r.top, width: m.r.width })); setOnCard(m.onCard); }
      else { setTransform(`translate(0px, ${-Math.round(window.innerHeight * 0.3)}px) scale(0.6)`); setLanded(true); }
      setPhase('glide');
      html.setAttribute('data-intro', 'in');
      t2 = window.setTimeout(() => { if (!cancelled) setPhase('done'); }, INTRO_GLIDE_MS + 40);
    };
    const t0 = Date.now();
    let fontsReady = false;
    const fonts = typeof document.fonts?.ready?.then === 'function' ? document.fonts.ready : Promise.resolve();
    Promise.race([fonts, new Promise((res) => setTimeout(res, 800))]).then(() => { fontsReady = true; });
    let last: DOMRect | null = null;
    const tick = () => {
      if (cancelled) return;
      const m = measure();
      const waited = Date.now() - t0;
      const settled = fontsReady && meLoadedRef.current && m !== null && last !== null && same(last, m.r);
      if (waited >= INTRO_HOLD_MS && (settled || waited >= INTRO_HOLD_MS + INTRO_SETTLE_CAP_MS)) go(m);
      else { last = m?.r ?? null; requestAnimationFrame(tick); }
    };
    requestAnimationFrame(tick);
    return () => { cancelled = true; clearTimeout(t2); };
  }, [state]);

  // The welcome: the mark holds, the tagline appears, then the cards.
  useEffect(() => {
    if (!state.welcome) return;
    // Never past 'leaving': the account's "already seen" answer can close the welcome before these beats land, and a beat
    // that then set 'cards' would bring the sheet back with its buttons dead (leaving.current already true).
    const beat = (next: 'tagline' | 'cards') => setPhase((p) => (p === 'leaving' || p === 'done' ? p : next));
    const t1 = setTimeout(() => beat('tagline'), state.kind === 'none' ? 0 : WELCOME_TAGLINE_AT_MS);
    const t2 = setTimeout(() => beat('cards'), state.kind === 'none' ? 300 : WELCOME_CARDS_AT_MS);
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, [state]);

  // The account's flag, once it loads: a reinstall on an account that has seen it closes the welcome quietly; a phone
  // that has seen it tells the account, so a reinstall won't show it again.
  const serverSeen = s.me ? !!s.me.welcomeSeen : null;
  useEffect(() => {
    const what = welcomeSync({ localSeen: readLocalSeen(), serverSeen });
    if (what === 'tell_server' && !toldServer) {
      toldServer = true;
      api('/api/me/welcome-seen', { body: {} }).catch(() => { toldServer = false; });
    } else if (what === 'remember') {
      rememberSeen();
      if (state.welcome && phase !== 'done') leave();
    }
  }, [serverSeen]); // eslint-disable-line react-hooks/exhaustive-deps

  const leave = () => {
    if (leaving.current) return;
    leaving.current = true;
    rememberSeen();
    if (s.me && !s.me.welcomeSeen && !toldServer) {
      toldServer = true;
      api('/api/me/welcome-seen', { body: {} }).catch(() => { toldServer = false; });
    }
    setPhase('leaving');
    document.documentElement.setAttribute('data-intro', 'in');
    setTimeout(() => setPhase('done'), INTRO_FADE_MS + 40);
  };
  const join = () => { leave(); navigate('/join?next=/', { root: false }); };

  if (phase === 'done') return null;
  const showWelcome = state.welcome && phase !== 'glide';
  return createPortal(
    <div className={`intro ${showWelcome ? 'welcome' : ''} ${phase === 'leaving' ? 'leaving' : ''} ${state.reduced ? 'still' : ''}`} data-phase={phase} style={{ '--lift': `${Math.round(window.innerHeight * 0.13) - state.at.top}px` } as React.CSSProperties} role={showWelcome ? 'dialog' : undefined} aria-label={showWelcome ? 'Welcome to Tikkas' : undefined} aria-hidden={showWelcome ? undefined : true}>
      <div className="intro-glow" />
      <Logo className={`intro-mark ${landed ? 'off' : ''} ${onCard ? 'tocard' : ''}`} style={{ left: state.at.left, top: state.at.top, width: MARK_WIDTH, transform: transform || undefined }} />
      {showWelcome ? (
        <>
          <button type="button" className="intro-skip" onClick={leave}>Skip</button>
          {v.welcome.tagline ? <p className={`intro-tagline display ${phase === 'hold' ? 'wait' : ''}`} style={{ top: state.at.top + MARK_HEIGHT + 18 }}>{v.welcome.tagline}</p> : null}
          {phase === 'cards' || phase === 'leaving' ? <WelcomeCards onJoin={join} onBrowse={leave} joinLabel={t('welcome.join')} browseLabel={t('welcome.browse')} /> : null}
        </>
      ) : null}
    </div>,
    document.body,
  );
}

function WelcomeCards({ onJoin, onBrowse, joinLabel, browseLabel }: { onJoin: () => void; onBrowse: () => void; joinLabel: string; browseLabel: string }) {
  const { v } = useLook();
  const cards = v.welcome.cards;
  const row = useRef<HTMLDivElement>(null);
  const [i, setI] = useState(0);
  const onScroll = () => {
    const el = row.current;
    if (el && el.clientWidth) setI(Math.max(0, Math.min(cards.length - 1, Math.round(el.scrollLeft / el.clientWidth))));
  };
  const go = (n: number) => row.current?.scrollTo({ left: n * row.current.clientWidth, behavior: 'smooth' });
  return (
    <div className="wsheet">
      <div className="wcards" ref={row} onScroll={onScroll} data-hscroll>
        {cards.map((c) => (
          <section className="wcard" key={c.id} aria-label={c.title}>
            {c.imageUrl ? <img src={c.imageUrl.startsWith('/api/') ? apiUrl(c.imageUrl) : c.imageUrl} alt="" /> : <span className="wph" />}
            <h2 className="display">{c.title}</h2>
            {c.text ? <p>{c.text}</p> : null}
          </section>
        ))}
      </div>
      <div className="wnav">
        <span className="wdots" aria-hidden="true">{cards.map((c, n) => <i key={c.id} className={n === i ? 'on' : ''} />)}</span>
        {i < cards.length - 1 ? <button type="button" className="wnext" onClick={() => go(i + 1)}>Next <Svg html={IC.chev} /></button> : <span />}
      </div>
      <button type="button" className="yellow" onClick={onJoin}>{joinLabel}</button>
      <button type="button" className="ghost wbrowse" onClick={onBrowse}>{browseLabel}</button>
    </div>
  );
}
